import { motion } from 'framer-motion';
import styles from './Experience.module.scss';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { useEffect, useRef } from 'react';

const Experience = () => {
  const { t } = useTranslation();
  const experiences = t('experience.items', { returnObjects: true });
  const education = t('about.education.items', { returnObjects: true });

  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const timelineItemsRef = useRef([]);

  const adjustDotPositions = () => {
    timelineItemsRef.current.forEach(item => {
      if (!item) return;
      
      const companyName = item.querySelector('h3');
      const dot = item.querySelector(`.${styles.timelineDot}`);
      
      if (companyName && dot) {
        const companyNameRect = companyName.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        
        // Adjust offset based on screen size
        const isMobile = window.innerWidth <= 768;
        const offsetAdjustment = isMobile ? -2 : -4; // Less offset for mobile
        
        const dotTop = companyNameRect.top - itemRect.top + (companyNameRect.height / 2) + offsetAdjustment;
        dot.style.top = `${dotTop}px`;
      }
    });
  };

  useEffect(() => {
    adjustDotPositions();
    window.addEventListener('resize', adjustDotPositions);
    
    return () => {
      window.removeEventListener('resize', adjustDotPositions);
    };
  }, [experiences]);

  return (
    <section ref={ref} className={styles.experience} id="experience">
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.8 }}
        className={styles.content}
      >
        <section className={styles.section}>
          <h2>{t('experience.title')}</h2>
          <div className={styles.timeline}>
            {experiences.map((exp, index) => (
              <motion.div 
                key={index}
                className={styles.timelineItem}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                ref={el => timelineItemsRef.current[index] = el}
              >
                <div className={styles.timelineDot} />
                <div className={styles.timelineContent}>
                  <div className={styles.header}>
                    {exp.logo && (
                      <div className={styles.logoContainer}>
                        <img src={exp.logo} alt={`${exp.company} logo`} />
                      </div>
                    )}
                    <div className={styles.headerText}>
                      <h3>{exp.company}</h3>
                      <span className={styles.period}>{exp.period}</span>
                    </div>
                  </div>
                  <h4>{exp.role}</h4>
                  <ul>
                    {exp.details.map((detail, idx) => (
                      <li key={idx}>{detail}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </motion.div>
    </section>
  );
};

export default Experience; 